import { useCallback, useEffect, useState } from 'react';

const HISTORY_KEY = 'yh_history';
const HISTORY_EVENT = 'yh-history-change';
const MAX_HISTORY = 5;

function readHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useHistory() {
  const [history, setHistory] = useState<string[]>(() => readHistory());

  useEffect(() => {
    const sync = () => setHistory(readHistory());
    window.addEventListener(HISTORY_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(HISTORY_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const addHistory = useCallback((id: string) => {
    try {
      const current = readHistory();
      const next = [id, ...current.filter((item) => item !== id)].slice(0, MAX_HISTORY);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      setHistory(next);
      window.dispatchEvent(new Event(HISTORY_EVENT));
    } catch {
      // ignore storage failures
    }
  }, []);

  const clearHistory = useCallback(() => {
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
    window.dispatchEvent(new Event(HISTORY_EVENT));
  }, []);

  return { history, addHistory, clearHistory };
}